import React from "react";
import { useNavigate } from "react-router-dom";
import { ScanLine, MessageCircleQuestion, TreePine, Leaf, ShoppingCart, Camera } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function HomePage({ deviceId }) {
  const navigate = useNavigate();

  const actions = [
    {
      icon: Camera,
      title: "Identify a Plant",
      desc: "Snap a photo to learn about any plant",
      path: "/identify",
      color: "bg-[#0A2918] text-white",
      testId: "action-identify",
    },
    {
      icon: MessageCircleQuestion,
      title: "Ask a Question",
      desc: "Get expert gardening advice",
      path: "/ask",
      color: "bg-white text-[#0A2918] border-2 border-[#0A2918]",
      testId: "action-ask",
    },
    {
      icon: TreePine,
      title: "Plan Your Yard",
      desc: "Upload a photo of your yard for a custom plan",
      path: "/yard-plan",
      color: "bg-[#28593A] text-white",
      testId: "action-yard-plan",
    },
  ];

  return (
    <div className="px-5 pt-8 pb-4" data-testid="home-page">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-[#0A2918] flex items-center justify-center hard-shadow-sm">
            <Leaf size={26} strokeWidth={2.5} className="text-white" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0A2918] tracking-tight" style={{ fontFamily: 'Work Sans' }}>
              The Garden Guide
            </h1>
          </div>
        </div>
        <p className="text-lg text-[#2A4D3B] font-medium mt-3 leading-relaxed">
          Your pocket plant expert. Identify plants, get care tips, and build your perfect garden.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="space-y-4 mb-8">
        {actions.map((action, i) => {
          const Icon = action.icon;
          return (
            <button
              key={action.path}
              onClick={() => navigate(action.path)}
              data-testid={action.testId}
              className={`w-full ${action.color} rounded-2xl p-5 flex items-center gap-4 text-left hard-shadow press-active transition-all duration-200 animate-slide-up`}
              style={{ animationDelay: `${i * 100}ms` }}
            >
              <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Icon size={28} strokeWidth={2.5} />
              </div>
              <div>
                <h3 className="text-xl font-bold" style={{ fontFamily: 'Work Sans' }}>{action.title}</h3>
                <p className="text-base opacity-80 mt-0.5">{action.desc}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Quick Access Row */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => navigate("/my-plants")}
          data-testid="quick-my-plants"
          className="bg-[#F2F7F4] border-2 border-[#0A2918] rounded-2xl p-4 flex flex-col items-center gap-2 hard-shadow-sm press-active"
        >
          <Leaf size={28} strokeWidth={2.5} className="text-[#28593A]" />
          <span className="text-base font-bold text-[#0A2918]">My Plants</span>
        </button>
        <button
          onClick={() => navigate("/shopping-list")}
          data-testid="quick-shopping-list"
          className="bg-[#F2F7F4] border-2 border-[#0A2918] rounded-2xl p-4 flex flex-col items-center gap-2 hard-shadow-sm press-active"
        >
          <ShoppingCart size={28} strokeWidth={2.5} className="text-[#28593A]" />
          <span className="text-base font-bold text-[#0A2918]">Shopping List</span>
        </button>
      </div>

      {/* Tip of the Day */}
      <div className="mt-8 bg-[#E25F45] text-white rounded-2xl p-5 hard-shadow-accent animate-slide-up" style={{ animationDelay: '300ms' }} data-testid="tip-card">
        <p className="text-sm font-bold uppercase tracking-wider mb-1" style={{ fontFamily: 'Work Sans' }}>Tip of the Day</p>
        <p className="text-base font-medium leading-relaxed">
          Water your garden early in the morning to reduce evaporation and give plants time to absorb moisture before the heat of the day.
        </p>
      </div>
    </div>
  );
}
